// node module
import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LOCATION_INITIALIZED } from '@angular/common';
import {
    BrowserAnimationsModule,
    NoopAnimationsModule,
} from '@angular/platform-browser/animations';
import { lastValueFrom } from 'rxjs';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { MimicViewerModule, MimicActionsService } from 'mimic-viewer';

import { ToastrModule } from 'ngx-toastr';
import { EchartsxModule } from 'echarts-for-angular';
import { DragDropModule } from '@angular/cdk/drag-drop';

// environments module
import { environment } from '@environments/environment';
import { RuntimeEnvironments } from '@environments/runtimeEnviroments.enum';

// route fold module
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { routing } from './app.routing';
import { MaterialModule } from './material.module';
import { OpsRouteReuseStrategy } from './ops-route-reuse-strategy';

// helpers module
import { AlarmDataFilterPipe, AlarmDataSortPipe } from '@helpers/alarm-utils';
import { Utils } from '@helpers/utils';
import { EndPointApi } from '@helpers/endpointapi';
import { httpInterceptorProviders } from '@helpers/auth-interceptor';
import { filterTrendGraph } from '@helpers/filterTrendGraph.pipe';

// directvies module
import { AlarmLevelColorDirective } from './directives/alarm-level-color/alarm-level-color.directive';
import { ScaledNumericInputDirective } from './directives/scaled-numeric-input/scaled-numeric-input.directive';
import { DynamicMaxHeightDirective } from './directives/alarm-table-height/alarm-table-height.directive';

// layout module
import { OpsHeaderComponent } from './layout/ops-header/ops-header.component';
import { OpsMimicsComponent } from './layout/ops-mimics/ops-mimics.component';
import { OpsSidebarComponent } from './layout/ops-sidebar/ops-sidebar.component';
import { MimicsGroupSubmenuComponent } from './layout/ops-sidebar/mimics-group-submenu.component';
import { OpsHeaderLatestAlarmComponent } from './layout/ops-header/ops-header-latest-alarm/ops-header-latest-alarm.component';
import { AlarmWindowComponent } from './layout/ops-header/alarm-window/alarm-window.component';
import { OpsBottombarComponent } from './layout/ops-bottombar/ops-bottombar.component';
import { EnvironmentSettingsComponent } from './layout/ops-header/environment-settings/environment-settings.component';
import { MimicsViewsMenuComponent } from './layout/ops-sidebar/mimics-views-menu/mimics-views-menu.component';

// components module
import { AlarmRangeListComponent } from '@components/alarm-range-list/alarm-range-list.component';
import { AlarmSettingFormComponent } from '@components/alarm-setting-form/alarm-setting-form.component';
import { AlarmStatusIconComponent } from '@components/alarm-status-icon/alarm-status-icon.component';
import { AlarmTableHeaderComponent } from '@components/alarm-table-header/alarm-table-header.component';
import { AlarmTableHeaderCompactComponent } from '@components/alarm-table-header-compact/alarm-table-header-compact.component';
import { AlarmTableRowComponent } from '@components/alarm-table-row/alarm-table-row.component';
import { AlarmTableRowCompactComponent } from '@components/alarm-table-row-compact/alarm-table-row-compact.component';
import { BubbleItemListComponent } from '@components/bubble-item-list/bubble-item-list.component';
import { BubbleSelectorComponent } from '@components/bubble-selector/bubble-selector.component';
import { CalendarComponent } from '@components/calendar/calendar.component';
import { ChannelAdvancedSettingsFormComponent } from '@components/channel-advanced-settings-form/channel-advanced-settings-form.component';
import { ChannelAlarmRangeComponent } from '@components/channel-alarm-range/channel-alarm-range.component';
import { ChannelGraphComponent } from '@components/channel-graph/channel-graph.component';
import { ChannelSettingsFormComponent } from '@components/channel-settings-form/channel-settings-form.component';
import { ChannelTableHeaderComponent } from '@components/channel-table-header/channel-table-header.component';
import { ChannelTableHeaderCompactComponent } from '@components/channel-table-header-compact/channel-table-header-compact.component';
import { ChannelTableRowComponent } from '@components/channel-table-row/channel-table-row.component';
import { ChannelTableRowCompactComponent } from '@components/channel-table-row-compact/channel-table-row-compact.component';
import { ConfirmationWindowComponent } from '@components/confirmation-window/confirmation-window.component';
import { DashboardPanelAddFormComponent } from '@components/dashboard-panel-add-form/dashboard-panel-add-form.component';
import { DashboardPanelComponent } from '@components/dashboard-panel/dashboard-panel.component';
import { DashboardPanelAlarmsComponent } from '@components/dashboard-panel-alarms/dashboard-panel-alarms.component';
import { DashboardPanelChannelGroupComponent } from '@components/dashboard-panel-channel-group/dashboard-panel-channel-group.component';
import { DashboardPanelHeaderComponent } from '@components/dashboard-panel-header/dashboard-panel-header.component';
import { DashboardPanelMimicsComponent } from '@components/dashboard-panel-mimics/dashboard-panel-mimics.component';
import { DashboardPanelTrendGraphComponent } from '@components/dashboard-panel-trend-graph/dashboard-panel-trend-graph.component';
import { FormAccordionComponent } from '@components/form-accordion/form-accordion.component';
import { LabelledBlockInputWrapperComponent } from '@components/labelled-block-input-wrapper/labelled-block-input-wrapper.component';
import { LanguageTextboxComponent } from '@components/language-textbox/language-textbox.component';
import { LeavePageConfirmationComponent } from '@components/leave-page-confirmation/leave-page-confirmation.component';
import { OpsAccordionComponent } from '@components/ops-accordion/ops-accordion.component';
import { OpsSpinnerInputComponent } from '@components/ops-spinner-input/ops-spinner-input.component';
import { OpsTableBodyComponent } from '@components/ops-table-body/ops-table-body.component';
import { PageListComponent } from '@components/page-list/page-list.component';
import { SearchChannelGroupComponent } from '@components/search-channel-group/search-channel-group.component';
import { SearchDropdownComponent } from '@components/search-dropdown/search-dropdown.component';
import { SignOutComponent } from '@components/signin/sign-out/sign-out.component';
import { SigninPasswordComponent } from '@components/signin/signin-password/signin-password.component';
import { SigninRoleComponent } from '@components/signin/signin-role/signin-role.component';
import { SwitchAccountsComponent } from '@components/signin/switch-accounts/switch-accounts.component';
import { TableLoadingComponent } from '@components/table-loading/table-loading.component';
import { ValidationErrorLabelComponent } from '@components/validation-error-label/validation-error-label.component';
import { VesselSelectListComponent } from '@components/vessel-select-list/vessel-select-list.component';
import { VesselBuilderSelectListComponent } from '@components/vessel-builder-select-list/vessel-builder-select-list.component';
import { DashboardAlarmCompact } from '@components/dashboard-alarm-compact/dashboard-alarm-compact.component';
import { PopUpNotificationComponent } from '@components/pop-up-notification/pop-up-notification.component';
import { LoadingSpinnerComponent } from '@components/loading-spinner/loading-spinner.component';
import { AlarmHistoryTableRowComponent } from '@components/alarm-history-table-row/alarm-history-table-row.component';
import { AlarmHistoryTableRowCompactComponent } from '@components/alarm-history-table-row-compact/alarm-history-table-row-compact.component';
import { AlarmHistoryTableRowWrapComponent } from '@components/alarm-history-table-row-wrap/alarm-history-table-row-wrap.component';
import { AlarmHistoryDetailOverlayComponent } from '@components/alarm-history-detail-overlay/alarm-history-detail-overlay.component';
import { InputSearchWrapperComponent } from '@components/input-search-wrapper/input-search-wrapper.component';
import { OperationTableHeaderComponent } from '@components/operation-table-header/operation-table-header.component';
import { OperationTableHeaderCompactComponent } from '@components/operation-table-header-compact/operation-table-header-compact.component';
import { OperationTableRowComponent } from '@components/operation-table-row/operation-table-row.component';
import { OperationTableRowCompactComponent } from '@components/operation-table-row-compact/operation-table-row-compact.component';
import { OperationTableRowWrapComponent } from '@components/operation-table-row-wrap/operation-table-row-wrap.component';
import { OperationDetailOverlayComponent } from '@components/operation-detail-overlay/operation-detail-overlay.component';
import { OpsAlarmTopbarXsmallComponent } from '@components/ops-alarm-topbar-xsmall/ops-alarm-topbar-xsmall.component';
import { OpsAlarmToTopComponent } from '@components/ops-alarm-to-top/ops-alarm-to-top.component';
import { FilterbarComponent } from '@components/filterbar/filterbar.component';
import { ChannelTableRowWrapComponent } from '@components/channel-table-row-wrap/channel-table-row-wrap.component';
import { ChannelSettingsDropdownComponent } from '@components/channel-settings-dropdown/channel-settings-dropdown.component';
import { ReposeToggleComponent } from '@components/repose-toggle/repose-toggle.component';
import { AlarmIndicatorComponent } from '@components/alarm-indicator/alarm-indicator.component';
import { DashboardPreviewComponent } from '@components/dashboard-preview/dashboard-preview.component';
import { CylinderTemperatureComponent } from '@components/cylinder-temperature/cylinder-temperature.component';
import { CylinderDeviationComponent } from '@components/cylinder-deviation/cylinder-deviation.component';
import { TurboChargerComponent } from '@components/turbo-charger/turbo-charger.component';
import { GaugeGraphComponent } from '@components/gauge-graph/gauge-graph.component';
import { PageShareSettingsComponent } from '@components/page-share-settings/page-share-settings.component';
import { UserShareSettingsComponent } from '@components/user-share-settings/user-share-settings.component';
import { PageStatusIndicatorComponent } from '@components/page-status-indicator/page-status-indicator.component';
import { EapStatusIndicatorComponent } from '@components/eap-status-indicator/eap-status-indicator.component';
import { OpsStatusIndicatorComponent } from '@components/ops-status-indicator/ops-status-indicator.component';
import { PrinterStatusIndicatorComponent } from '@components/printer-status-indicator/printer-status-indicator.component';
import { CommsStatusIndicatorComponent } from '@components/comms-status-indicator/comms-status-indicator.component';
import { CommsListStatusIndicatorComponent } from '@components/comms-list-status-indicator/comms-list-status-indicator.component';
import { PortsStatusIndicatorComponent } from '@components/ports-status-indicator/ports-status-indicator.component';
import { FcuListStatusIndicatorComponent } from '@components/fcu-list-status-indicator/fcu-list-status-indicator.component';
import { FcuStatusIndicatorComponent } from '@components/fcu-status-indicator/fcu-status-indicator.component';
import { FuListStatusIndicatorComponent } from '@components/fu-list-status-indicator/fu-list-status-indicator.component';
import { FuStatusIndicatorComponent } from '@components/fu-status-indicator/fu-status-indicator.component';
import { FuSlotStatusIndicatorComponent } from '@components/fu-slot-status-indicator/fu-slot-status-indicator.component';
import { OverviewSystemErrorIndicatorComponent } from '@components/overview-system-error-indicator/overview-system-error-indicator.component';
import { ProjectSettingsFormComponent } from '@components/project-settings-form/project-settings-form.component';
import { AzureLoginPromptComponent } from '@components/azure-login-prompt/azure-login-prompt.component';
import { BlobVersionSelectorComponent } from '@components/blob-version-selector/blob-version-selector.component';
import { AlarmTableRowWrapComponent } from '@components/alarm-table-row-wrap/alarm-table-row-wrap.component';
import { AlarmMonitoringDetailOverlayComponent } from '@components/alarm-monitoring-detail-overlay/alarm-monitoring-detail-overlay.component';
import { ScrollNavigatorComponent } from '@components/scroll-navigator/scroll-navigator.component';
import { UsedTitlesSuggestionsComponent } from '@components/used-titles-suggestions/used-titles-suggestions.component';
import { TextAreaComponent } from '@components/textarea/textarea.component';
import { CustomPageDescriptionComponent } from '@components/custom-page-description/custom-page-description.component';
import { CustomPageRightButtonsComponent } from '@components/custom-page-right-buttons/custom-page-right-buttons.component';
import { DescriptionDialogComponent } from '@components/description-dialog/description-dialog.component';
import { ForbiddenNotificationComponent } from '@components/forbidden-notification/forbidden-notification.component';
import { SelectDropDownComponent } from '@components/select-dropdown/select-dropdown.component';
import { EditPageSearchDropdownComponent } from '@components/edit-page-search-dropdown/edit-page-search-dropdown.component';

// services module
import { AlarmHistoryService } from '@service/ops-alarms/alarm-history.service';
import { OpsAlarmMonitoringService } from '@service/ops-alarms/ops-alarm-monitoring.service';
import { OperationsHistoryService } from '@service/ops-operations-history-services/operations-history-service.service';
import { AlarmAnimationService } from '@service/alarm-animation.service';
import { AnimationTimingService } from '@service/animation-timing.service';
import { AuthService } from '@service/auth.service';
import { ChannelGroupApiService } from '@service/channel-group-api.service';
import { ChannelGroupService } from '@service/channel-groups.service';
import { ChannelHistoryService } from '@service/channel-history.service';
import { ChannelService } from '@service/channel.service';
import { ConfirmationPromptService } from '@service/confirmation-prompt.service';
import { CssVarService } from '@service/css-vars.service';
import { CustomGroupsService } from '@service/custom-groups.service';
import { DashboardPanelFactoryService } from '@service/dashboard-panel-factory-service';
import { DashboardViewService } from '@service/dashboard-view.service';
import { DashboardViewApiService } from '@service/dashboard-view-api.service';
import { DeviceFlagsService } from '@service/device-flags.service';
import { FitToContainerPaginationStrategy } from '@service/fit-to-container-pagination-strategy';
import { LanguageService } from '@service/language.service';
import { NavigationService } from '@service/navigation.service';
import { NotificationService } from '@service/notification.service';
import { SocketService } from '@service/socket.service';
import { TrendGraphSettingsService } from '@service/trend-graph-settings.service';
import { TrendGraphViewsService } from '@service/trend-graph-views.service';
import { TrendGraphViewsApiService } from '@service/trend-graph-views-api.service';
import { HmiService } from '@service/hmi.service';
import { ThemeService } from '@service/theme.service';
import { SimulatorService } from '@service/simulator.service';
import { GraphsApiService } from '@service/graphs-api.service';
import { GraphsService } from '@service/graphs.service';
import { TrendGraphAccessSettingsService } from '@service/trend-graph-access-settings.service';
import { CustomGroupAccessSettingsService } from '@service/custom-group-access-settings.service';
import { SystemStatusRoutingService } from '@service/system-status-routing.service';
import { SystemStatusService } from '@service/system-status.service';
import { EmptyBlobStorageErrorComponent } from '@service/project-settings/azure-storage.service';
import { SocketMiddlewareService } from '@service/socket-middleware.service';
import { ResponsiveService } from '@service/responsive.service';

// pages module
import { OpsAlarmComponent } from './pages/ops-alarm/ops-alarm.component';
import { OpsAlarmHistoryComponent } from './pages/ops-alarm/ops-alarm-history/ops-alarm-history.component';
import { OpsAlarmMonitoringComponent } from './pages/ops-alarm/ops-alarm-monitoring/ops-alarm-monitoring.component';
import { OpsOperationsComponent } from './pages/ops-alarm/ops-operations/ops-operations.component';
import { OpsChannelViewsComponent } from './pages/ops-channel-views/ops-channel-views.component';
import { OpsChannelListComponent } from './pages/ops-channel-views/ops-channel-list/ops-channel-list.component';
import { OpsGroupsComponent } from './pages/ops-channel-views/ops-groups/ops-groups.component';
import { OpsNewCustomGroupComponent } from './pages/ops-channel-views/ops-new-custom-group/ops-new-custom-group.component';
import { OpsDashboardComponent } from './pages/ops-dashboard/ops-dashboard.component';
import { OpsTrendGraphComponent } from './pages/ops-trend-graph/ops-trend-graph.component';
import { TrendGraphCreateComponent } from './pages/ops-trend-graph/trend-graph-create/trend-graph-create.component';
import { TrendGraphViewComponent } from './pages/ops-trend-graph/trend-graph-view/trend-graph-view.component';
import { OpsGraphsComponent } from './pages/ops-graphs/ops-graphs.component';
import { BarGraphComponent } from './pages/ops-graphs/bar-graph/bar-graph.component';
import { ExhaustGraphComponent } from './pages/ops-graphs/exhaust-graph/exhaust-graph.component';
import { AnalogGraphComponent } from './pages/ops-graphs/analog-graph/analog-graph.component';
import { OpsSystemStatusComponent } from './pages/ops-system-status/ops-system-status.component';
import { OpsStatusSideMenuComponent } from './pages/ops-system-status/ops-status-side-menu/ops-status-side-menu.component';
import { OverviewComponent } from './pages/ops-system-status/pages/overview/overview.component';
import { EapStatusComponent } from './pages/ops-system-status/pages/eap-status/eap-status.component';
import { OpsStatusComponent } from './pages/ops-system-status/pages/ops-status/ops-status.component';
import { PrintersStatusComponent } from './pages/ops-system-status/pages/printers-status/printers-status.component';
import { CommsStatusComponent } from './pages/ops-system-status/pages/comms-status/comms-status.component';
import { FcuStatusComponent } from './pages/ops-system-status/pages/fcu-status/fcu-status.component';
import { CustomGraphCreateComponent } from '@pages/ops-graphs/custom-graph-create/custom-graph-create.component';
import { CustomGraphEmptyComponent } from '@components/custom-graph-empty/custom-graph-empty.component';
import { CustomGraphDialogComponent } from '@pages/ops-graphs/custom-graph-dialog/custom-graph-dialog.component';
import { CustomBarGraphComponent } from '@components/custom-bar-graph/custom-bar-graph.component';
import { CustomIndicatorGraphComponent } from '@components/custom-indicator-graph/custom-indicator-graph.component';
import { CustomGraphService } from '@service/custom-graph.service';
import { CustomGraphAccessSettingsService } from '@service/custom-graph-access-settings.service';
import { VesselService } from '@service/vessel.service';
import { MimicConfirmationNotificationComponent } from './components/mimic-confirmation-notification/mimic-confirmation-notification.component';
import { TextInputComponent } from '@components/input/text-input.component';
import { DisconnectedSnackBarComponent } from '@components/disconnected-snack-bar/disconnected-snack-bar.component';

function createTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
    declarations: [
        //Pipes
        filterTrendGraph,
        AlarmDataFilterPipe,
        AlarmDataSortPipe,
        //Components
        AppComponent,
        AlarmRangeListComponent,
        AlarmSettingFormComponent,
        AlarmStatusIconComponent,
        AlarmTableHeaderComponent,
        AlarmTableHeaderCompactComponent,
        AlarmTableRowComponent,
        AlarmTableRowCompactComponent,
        DashboardAlarmCompact,
        BubbleItemListComponent,
        BubbleSelectorComponent,
        CalendarComponent,
        ChannelAdvancedSettingsFormComponent,
        ChannelAlarmRangeComponent,
        ChannelGraphComponent,
        ChannelSettingsFormComponent,
        ChannelTableHeaderComponent,
        ChannelTableHeaderCompactComponent,
        ChannelTableRowComponent,
        ChannelTableRowCompactComponent,
        ConfirmationWindowComponent,
        DashboardPanelAddFormComponent,
        DashboardPanelComponent,
        DashboardPanelAlarmsComponent,
        DashboardPanelChannelGroupComponent,
        DashboardPanelHeaderComponent,
        DashboardPanelMimicsComponent,
        DashboardPanelTrendGraphComponent,
        FormAccordionComponent,
        LabelledBlockInputWrapperComponent,
        LanguageTextboxComponent,
        LeavePageConfirmationComponent,
        OpsAccordionComponent,
        OpsSpinnerInputComponent,
        OpsTableBodyComponent,
        PageListComponent,
        SearchChannelGroupComponent,
        SearchDropdownComponent,
        SignOutComponent,
        SigninPasswordComponent,
        SigninRoleComponent,
        SwitchAccountsComponent,
        TableLoadingComponent,
        ValidationErrorLabelComponent,
        MimicsGroupSubmenuComponent,
        OpsHeaderComponent,
        OpsHeaderLatestAlarmComponent,
        AlarmWindowComponent,
        OpsMimicsComponent,
        OpsSidebarComponent,
        OpsAlarmComponent,
        OpsAlarmHistoryComponent,
        OpsAlarmMonitoringComponent,
        OpsOperationsComponent,
        OpsChannelViewsComponent,
        OpsChannelListComponent,
        OpsGroupsComponent,
        OpsNewCustomGroupComponent,
        OpsDashboardComponent,
        OpsTrendGraphComponent,
        TrendGraphCreateComponent,
        TrendGraphViewComponent,
        OpsTrendGraphComponent,
        AlarmLevelColorDirective,
        ScaledNumericInputDirective,
        DynamicMaxHeightDirective,
        ProjectSettingsFormComponent,
        VesselSelectListComponent,
        VesselBuilderSelectListComponent,
        AzureLoginPromptComponent,
        BlobVersionSelectorComponent,
        EmptyBlobStorageErrorComponent,
        OpsBottombarComponent,
        EnvironmentSettingsComponent,
        AlarmTableRowWrapComponent,
        AlarmMonitoringDetailOverlayComponent,
        AlarmHistoryTableRowComponent,
        AlarmHistoryTableRowCompactComponent,
        AlarmHistoryTableRowWrapComponent,
        AlarmHistoryDetailOverlayComponent,
        InputSearchWrapperComponent,
        OperationTableHeaderComponent,
        OperationTableHeaderCompactComponent,
        OperationTableRowComponent,
        OperationTableRowCompactComponent,
        OperationTableRowWrapComponent,
        OperationDetailOverlayComponent,
        OpsAlarmTopbarXsmallComponent,
        OpsAlarmToTopComponent,
        PopUpNotificationComponent,
        LoadingSpinnerComponent,
        FilterbarComponent,
        ChannelTableRowWrapComponent,
        ChannelSettingsDropdownComponent,
        ReposeToggleComponent,
        AlarmIndicatorComponent,
        DashboardPreviewComponent,
        MimicsViewsMenuComponent,
        OpsGraphsComponent,
        BarGraphComponent,
        ExhaustGraphComponent,
        AnalogGraphComponent,
        CylinderTemperatureComponent,
        CylinderDeviationComponent,
        TurboChargerComponent,
        GaugeGraphComponent,
        PageShareSettingsComponent,
        UserShareSettingsComponent,
        OpsSystemStatusComponent,
        OpsStatusSideMenuComponent,
        OverviewComponent,
        EapStatusComponent,
        OpsStatusComponent,
        PageStatusIndicatorComponent,
        EapStatusIndicatorComponent,
        OpsStatusIndicatorComponent,
        PrinterStatusIndicatorComponent,
        PrintersStatusComponent,
        CommsStatusComponent,
        CommsStatusIndicatorComponent,
        CommsListStatusIndicatorComponent,
        PortsStatusIndicatorComponent,
        FcuListStatusIndicatorComponent,
        FcuStatusComponent,
        FcuStatusIndicatorComponent,
        FuListStatusIndicatorComponent,
        FuStatusIndicatorComponent,
        FuSlotStatusIndicatorComponent,
        OverviewSystemErrorIndicatorComponent,
        ScrollNavigatorComponent,
        UsedTitlesSuggestionsComponent,
        TextAreaComponent,
        CustomPageDescriptionComponent,
        CustomPageRightButtonsComponent,
        DescriptionDialogComponent,
        ForbiddenNotificationComponent,
        SelectDropDownComponent,
        EditPageSearchDropdownComponent,
        CustomGraphCreateComponent,
        CustomGraphEmptyComponent,
        CustomGraphDialogComponent,
        CustomBarGraphComponent,
        CustomIndicatorGraphComponent,
        MimicConfirmationNotificationComponent,
        TextInputComponent,
        DisconnectedSnackBarComponent,
    ],
    exports: [],
    bootstrap: [AppComponent],
    imports: [
        MimicViewerModule,
        ToastrModule.forRoot({
            timeOut: 3000,
            positionClass: 'toast-bottom-right',
            preventDuplicates: false,
        }),
        BrowserModule,
        AppRoutingModule,
        // CommonModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpClient],
            },
            defaultLanguage: 'en',
        }),
        FormsModule,
        ReactiveFormsModule,
        routing,
        MaterialModule,
        NoopAnimationsModule,
        BrowserAnimationsModule,
        EchartsxModule,
        DragDropModule,
    ],
    providers: [
        Utils,
        HmiService,
        AlarmHistoryService,
        OpsAlarmMonitoringService,
        OperationsHistoryService,
        AlarmAnimationService,
        AnimationTimingService,
        AuthService,
        ChannelGroupApiService,
        ChannelGroupService,
        ChannelHistoryService,
        ChannelService,
        ConfirmationPromptService,
        CssVarService,
        CustomGroupsService,
        DashboardPanelFactoryService,
        DashboardViewService,
        DashboardViewApiService,
        DeviceFlagsService,
        FitToContainerPaginationStrategy,
        LanguageService,
        NavigationService,
        NotificationService,
        SocketService,
        SocketMiddlewareService,
        TrendGraphSettingsService,
        TrendGraphViewsService,
        TrendGraphViewsApiService,
        ThemeService,
        ResponsiveService,
        SimulatorService,
        GraphsApiService,
        GraphsService,
        TrendGraphAccessSettingsService,
        CustomGroupAccessSettingsService,
        CustomGraphService,
        CustomGraphAccessSettingsService,
        { provide: RouteReuseStrategy, useClass: OpsRouteReuseStrategy },
        httpInterceptorProviders,
        SystemStatusService,
        SystemStatusRoutingService,
        MimicActionsService,
        {
            provide: APP_INITIALIZER,
            useFactory: ApplicationInitializerFactory,
            deps: [TranslateService, Injector, VesselService, SimulatorService],
            multi: true,
        },
        { provide: 'environment', useValue: environment },
        provideHttpClient(withInterceptorsFromDi()),
    ],
})
export class AppModule {}

export function ApplicationInitializerFactory(
    translate: TranslateService,
    injector: Injector,
    vesselService: VesselService,
    simulatorService: SimulatorService,
) {
    return async () => {
        await injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
        translate.setDefaultLang('en');
        await lastValueFrom(translate.use('en'));
        await vesselService.loadVessel();
        if (environment.env !== RuntimeEnvironments.ELECTRON) {
            simulatorService.checkSimulatorStatus();
        }
    };
}
